import { useState } from "react";
import { AuthLayout } from "../../signup/authlayout";
import { TextField } from "../../common/inputFiled";
import { PhoneField } from "../../common/phoneFiled";
import { CountryModal } from "../../../auth/countrymodal";
import { APP_SETUP_STATIC_INVITE_CODE } from "../../../config/appSetupConfig";

const NAME_INPUT_FILTER = /[^a-zA-Z\s']/g;
const NAME_ERROR = "Name can only contain letters, spaces, and apostrophes.";

const sanitizeNameInput = (value) => {
  const cleaned = String(value || "").replace(/^\s+/, "").replace(NAME_INPUT_FILTER, "");
  if (!cleaned) return "";
  return cleaned
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ");
};

const isValidName = (value) =>
  Boolean(value?.trim()) && /^[a-zA-Z\s']+$/.test(value.trim()) && /[a-zA-Z]/.test(value);

export default function AppSetupSignupStep({ onSubmit, loading }) {
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "+91",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked, countryCode, phoneNumber } = e.target;
    let newVal = type === "checkbox" ? checked : value;

    if (name === "firstName" || name === "lastName") {
      newVal = sanitizeNameInput(newVal);
    }

    const updated = {
      ...formData,
      [name]: newVal,
      ...(countryCode && { countryCode }),
      ...(phoneNumber && { phoneNumber }),
    };

    setFormData(updated);

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "password" || name === "confirmPassword") {
      const pass = updated.password || "";
      const confirm = updated.confirmPassword || "";
      if (confirm.length > 0 && pass !== confirm) {
        setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      } else if (errors.confirmPassword) {
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy.confirmPassword;
          return copy;
        });
      }
    }
  };

  const handleCountrySelect = (country) => {
    setFormData((prev) => ({
      ...prev,
      countryCode: `+${country.dialCode}`,
      phone: country.dialCode,
      phoneNumber: "",
    }));
    setShowCountryModal(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setErrors({ general: "Please fill in all required fields." });
      return;
    }

    if (!isValidName(formData.firstName)) {
      setErrors((prev) => ({ ...prev, firstName: NAME_ERROR }));
      return;
    }

    if (!isValidName(formData.lastName)) {
      setErrors((prev) => ({ ...prev, lastName: NAME_ERROR }));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: "Passwords do not match." }));
      return;
    }

    if (!formData.countryCode || !formData.phoneNumber) {
      setErrors({ general: "Please enter a valid phone number." });
      return;
    }

    if (!formData.agreeToTerms) {
      setErrors({ general: "Please agree to the terms & privacy policy." });
      return;
    }

    onSubmit(formData);
  };

  return (
    <>
      {showCountryModal && (
        <CountryModal
          onSelect={handleCountrySelect}
          onClose={() => setShowCountryModal(false)}
        />
      )}

      <AuthLayout
        title={
          <>
            JOIN <span className="text-[#643ECF]">PHONEBOOKPLUS</span>
          </>
        }
        subtitle="Manage contacts and chat in one place—link multiple numbers to a single profile and control exactly what your network sees."
      >
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <TextField
            id="firstName"
            type="text"
            name="firstName"
            label="First Name"
            required
            startIcon="./input-logos/user.svg"
            placeholder="Enter First Name"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
          />

          <TextField
            id="lastName"
            type="text"
            name="lastName"
            label="Last Name"
            required
            startIcon="./input-logos/user.svg"
            placeholder="Enter Last Name"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
          />

          <TextField
            id="email"
            type="email"
            name="email"
            label="Email"
            required
            startIcon="./input-logos/sms.svg"
            placeholder="Enter Email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            autoComplete="email"
          />

          <PhoneField
            name="phone"
            label="Phone"
            required
            value={formData.phone}
            onChange={handleChange}
            error={errors.phone}
            openCountryModal={() => setShowCountryModal(true)}
          />

          <TextField
            id="password"
            type="password"
            name="password"
            label="Password"
            required
            startIcon="./input-logos/password.svg"
            placeholder="My Password"
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            autoComplete="new-password"
          />

          <TextField
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            required
            startIcon="./input-logos/password.svg"
            placeholder="Confirm My Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={errors.confirmPassword}
            autoComplete="new-password"
          />

          <p className="text-xs text-gray-500 rounded-xl bg-violet-50 px-3 py-2">
            Registration uses invite code: <span className="font-semibold text-[#643ECF]">{APP_SETUP_STATIC_INVITE_CODE}</span>
          </p>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              name="agreeToTerms"
              required
              className="h-4 w-4 accent-[#7C4DFF] border-gray-300"
              checked={formData.agreeToTerms}
              onChange={handleChange}
            />
            <span className="text-sm text-gray-700">
              I agree with{" "}
              <a href="/term-condition" className="text-[#7C4DFF] underline">
                terms & conditions
              </a>{" "}
              and{" "}
              <a href="/privacy-policy" className="text-[#7C4DFF] underline">
                privacy policy
              </a>
            </span>
          </label>

          {errors.general && <p className="text-red-500 text-sm">{errors.general}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-3xl bg-gradient-to-b from-[#7c4ff9] to-[#47299c] py-3.5 text-white font-semibold shadow-lg shadow-[#7C4DFF]/25 hover:brightness-105 active:scale-[0.99] transition disabled:opacity-50"
          >
            {loading ? "Signing Up..." : "Continue"}
          </button>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{" "}
            <a href="/login" className="text-[#7C4DFF] underline">
              Sign in here
            </a>
          </p>
        </form>
      </AuthLayout>
    </>
  );
}
